import { z, isoZ } from 'my-zod'
import { productLineOptions } from '../../internal'

// tracking events
export const CustomerTrackingEventTypeEnum = z.enum([
  'CreateOrUpdateAccount',
  'SubscriptionStart',
  'SubscriptionFinish',
  'BookingStart',
  'BookingFinish',
  'CustomerVehicleRental',
  'CreditBalanceUpdate',
  'CreateOrUpdateOutfitterContact',
  'WebSignup'
])

export type CustomerTrackingEventType = z.infer<typeof CustomerTrackingEventTypeEnum>

// tracking data by events

export const BasicTrackingData = z.object({
  email: z.string()
})

export type BasicTrackingData = z.infer<typeof BasicTrackingData>

export const CreateOrUpdateAccountTrackingView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('CreateOrUpdateAccount'),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  market: z.string().nullable(),
  marketingOptIn: z.boolean().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  birthday: z.string().nullable()
})

export type CreateOrUpdateAccountTrackingView = z.infer<typeof CreateOrUpdateAccountTrackingView>

export const SubscriptionStartCustomerTrackingView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('SubscriptionStart'),
  firstName: z.string(),
  lastName: z.string(),
  primaryPhone: z.string(),
  market: z.string(),
  marketingOptIn: z.boolean(),
  postalCode: z.string(),
  plan: z.string()
})

export type SubscriptionStartCustomerTrackingView = z.infer<typeof SubscriptionStartCustomerTrackingView>

export const SubscriptionFinishCustomerTrackingView = BasicTrackingData.augment({
  type: z.literal('SubscriptionFinish'),
  customerId: z.string(),
  plan: z.string(),
  joinDateTime: isoZ.instant()
})

export type SubscriptionFinishCustomerTrackingView = z.infer<typeof SubscriptionFinishCustomerTrackingView>

export const BookingCustomerStartTrackingView = SubscriptionStartCustomerTrackingView.omit({
  id: true,
  marketingOptIn: true
}).augment({
  id: z.string(),
  type: z.literal('BookingStart'),
  adventureName: z.string(),
  adventureUrl: z.string(),
  adventureAddress: z.string()
})

export type BookingCustomerStartTrackingView = z.infer<typeof BookingCustomerStartTrackingView>

export const BookingCustomerFinishTrackingView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('BookingFinish')
})

export type BookingCustomerFinishTrackingView = z.infer<typeof BookingCustomerFinishTrackingView>

export const CustomerVehicleRentalTrackingView = BasicTrackingData.augment({
  type: z.literal('CustomerVehicleRental'),
  outfitterId: z.number(),
  outfitterName: z.string(),
  vehicleRidden: z.string(),
  productLineRidden: z.enum(productLineOptions),
  adventure: z.string(),
  vehicleUseHistoryId: z.string()
})

export type CustomerVehicleRentalTrackingView = z.infer<typeof CustomerVehicleRentalTrackingView>

export const CreditBalanceUpdateView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('CreditBalanceUpdate'),
  creditBalance: z.number()
})

export type CreditBalanceUpdateView = z.infer<typeof CreditBalanceUpdateView>

export const CreateOrUpdateOutfitterContactTrackingView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('CreateOrUpdateOutfitterContact'),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string(),
  decisionPower: z.string(),
  status: z.string(),
  departments: z.string(),
  lastModifiedDate: z.string()
})

export type CreateOrUpdateOutfitterContactTrackingView = z.infer<typeof CreateOrUpdateOutfitterContactTrackingView>

export const WebSignupTrackingView = BasicTrackingData.augment({
  id: z.string(),
  type: z.literal('WebSignup')
})

export type WebSignupTrackingView = z.infer<typeof WebSignupTrackingView>

export const CustomerTrackingView = z.union([
  CreateOrUpdateAccountTrackingView,
  SubscriptionStartCustomerTrackingView,
  SubscriptionFinishCustomerTrackingView,
  BookingCustomerStartTrackingView,
  BookingCustomerFinishTrackingView,
  CustomerVehicleRentalTrackingView,
  CreditBalanceUpdateView,
  CreateOrUpdateOutfitterContactTrackingView,
  WebSignupTrackingView
])

export type CustomerTrackingView = z.infer<typeof CustomerTrackingView>
